import React, { useState, useEffect } from 'react';
import { BarChart3, Calendar, Download, TrendingUp, TrendingDown, Loader, RefreshCw } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { firebaseService } from '../../services/firebaseService';
import { metaConsumptionService } from '../../services/metaConsumptionService';
import { MessageStatistics, DailyUsage } from '../../types/FireAgent';
import { useNotification } from '../../hooks/useNotification';

type PeriodType = 'today' | 'week' | 'month' | 'custom';

const Consumo: React.FC = () => {
  const { userProfile } = useAuth();
  const { showSuccess, showError, showInfo, NotificationComponent } = useNotification();
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState<MessageStatistics | null>(null);
  const [dailyUsage, setDailyUsage] = useState<DailyUsage[]>([]);
  const [period, setPeriod] = useState<PeriodType>('month');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  useEffect(() => {
    loadConsumption();
  }, [period, customStartDate, customEndDate]);

  const getDateRange = (): { start: string; end: string } => {
    const today = new Date();
    const end = new Date(today);
    end.setHours(23, 59, 59, 999);

    let start: Date;

    switch (period) {
      case 'today':
        start = new Date(today);
        start.setHours(0, 0, 0, 0);
        break;
      case 'week':
        start = new Date(today);
        start.setDate(today.getDate() - 7);
        start.setHours(0, 0, 0, 0);
        break;
      case 'month':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        start.setHours(0, 0, 0, 0);
        break;
      case 'custom':
        if (customStartDate && customEndDate) {
          return {
            start: customStartDate,
            end: customEndDate
          };
        }
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        start.setHours(0, 0, 0, 0);
        break;
      default:
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        start.setHours(0, 0, 0, 0);
    }

    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    };
  };

  const loadConsumption = async () => {
    try {
      setLoading(true);
      
      if (!userProfile?.empresaCnpj) {
        showError('Erro', 'CNPJ da empresa não encontrado');
        return;
      }

      const metaApiConfig = await firebaseService.getMetaApiConfig(userProfile.empresaCnpj);
      if (!metaApiConfig || !metaApiConfig.accessToken || !metaApiConfig.phoneNumberId) {
        showInfo('Configuração Necessária', 'Configure o Meta API em Configurações antes de visualizar o consumo.');
        setStatistics(null);
        setDailyUsage([]);
        return;
      }

      metaConsumptionService.setConfig(metaApiConfig);

      const dateRange = getDateRange();
      const stats = await metaConsumptionService.getMessageStatistics(dateRange.start, dateRange.end);
      const daily = await metaConsumptionService.getDailyUsage(dateRange.start, dateRange.end);

      setStatistics(stats);
      setDailyUsage(daily);
    } catch (error: any) {
      console.error('Erro ao carregar consumo:', error);
      // Se a API não suportar analytics, mostrar mensagem amigável
      if (error.message?.includes('analytics') || error.message?.includes('404')) {
        showInfo('Funcionalidade Indisponível', 'A API de analytics pode não estar disponível para sua conta. Verifique as permissões do Access Token.');
      } else {
        showError('Erro', error.message || 'Erro ao carregar dados de consumo');
      }
      setStatistics(null);
      setDailyUsage([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat('pt-BR').format(num);
  };

  const getSuccessRate = (): number => {
    if (!statistics || statistics.total_sent === 0) return 0;
    return Math.round((statistics.total_delivered / statistics.total_sent) * 100);
  };

  const exportToCSV = () => {
    if (!dailyUsage.length) {
      showInfo('Sem Dados', 'Não há dados para exportar');
      return;
    }

    const headers = ['Data', 'Enviadas', 'Entregues', 'Falhadas', 'Taxa de Sucesso (%)'];
    const rows = dailyUsage.map(day => {
      const successRate = day.sent > 0 ? Math.round((day.delivered / day.sent) * 100) : 0;
      return [
        formatDate(day.date),
        day.sent.toString(),
        day.delivered.toString(),
        day.failed.toString(),
        successRate.toString()
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `consumo_whatsapp_${period}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    showSuccess('Sucesso', 'Relatório exportado com sucesso');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Controle de Consumo</h1>
        <p className="text-gray-600">Monitore o uso dos serviços Meta API</p>
      </div>

      {/* Filtros de Período */}
      <div className="card p-6 mb-6">
        <div className="flex flex-col md:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Período
            </label>
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value as PeriodType)}
              className="input w-full"
            >
              <option value="today">Hoje</option>
              <option value="week">Últimos 7 dias</option>
              <option value="month">Mês atual</option>
              <option value="custom">Personalizado</option>
            </select>
          </div>
          {period === 'custom' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Data Inicial
                </label>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Data Final
                </label>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="input"
                />
              </div>
            </>
          )}
          <button
            onClick={loadConsumption}
            className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Atualizar
          </button>
          <button
            onClick={exportToCSV}
            disabled={!dailyUsage.length}
            className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            Exportar CSV
          </button>
        </div>
      </div>

      {!statistics ? (
        <div className="card p-12 text-center">
          <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">
            Não foi possível carregar os dados de consumo. Verifique se a API de analytics está disponível.
          </p>
        </div>
      ) : (
        <>
          {/* Cards de Métricas */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="card p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-600">Total Enviadas</span>
                <TrendingUp className="h-5 w-5 text-blue-600" />
              </div>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(statistics.total_sent)}</p>
            </div>
            <div className="card p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-600">Entregues</span>
                <TrendingUp className="h-5 w-5 text-green-600" />
              </div>
              <p className="text-2xl font-bold text-green-600">{formatNumber(statistics.total_delivered)}</p>
            </div>
            <div className="card p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-600">Falhadas</span>
                <TrendingDown className="h-5 w-5 text-red-600" />
              </div>
              <p className="text-2xl font-bold text-red-600">{formatNumber(statistics.total_failed)}</p>
            </div>
            <div className="card p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-600">Taxa de Sucesso</span>
                <BarChart3 className="h-5 w-5 text-blue-600" />
              </div>
              <p className="text-2xl font-bold text-blue-600">{getSuccessRate()}%</p>
            </div>
          </div>

          {/* Gráfico de Consumo Diário */}
          {dailyUsage.length > 0 && (
            <div className="card p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Consumo Diário</h2>
              <div className="space-y-3">
                {dailyUsage.map((day, index) => {
                  const successRate = day.sent > 0 ? Math.round((day.delivered / day.sent) * 100) : 0;
                  const maxValue = Math.max(day.sent, day.delivered, day.failed, 1);
                  
                  return (
                    <div key={index} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="font-medium text-gray-700">{formatDate(day.date)}</span>
                        <span className="text-gray-600">
                          {formatNumber(day.sent)} enviadas • {formatNumber(day.delivered)} entregues • {formatNumber(day.failed)} falhadas ({successRate}%)
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="flex h-2 rounded-full overflow-hidden">
                          <div
                            className="bg-green-600"
                            style={{ width: `${(day.delivered / maxValue) * 100}%` }}
                          />
                          <div
                            className="bg-red-600"
                            style={{ width: `${(day.failed / maxValue) * 100}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Tabela Detalhada */}
          {dailyUsage.length > 0 && (
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Detalhamento Diário</h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Data</th>
                      <th className="text-right py-3 px-4 font-medium text-gray-700">Enviadas</th>
                      <th className="text-right py-3 px-4 font-medium text-gray-700">Entregues</th>
                      <th className="text-right py-3 px-4 font-medium text-gray-700">Falhadas</th>
                      <th className="text-right py-3 px-4 font-medium text-gray-700">Taxa de Sucesso</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dailyUsage.map((day, index) => {
                      const successRate = day.sent > 0 ? Math.round((day.delivered / day.sent) * 100) : 0;
                      return (
                        <tr key={index} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4 text-gray-900">{formatDate(day.date)}</td>
                          <td className="py-3 px-4 text-right text-gray-700">{formatNumber(day.sent)}</td>
                          <td className="py-3 px-4 text-right text-green-600">{formatNumber(day.delivered)}</td>
                          <td className="py-3 px-4 text-right text-red-600">{formatNumber(day.failed)}</td>
                          <td className="py-3 px-4 text-right">
                            <span className={`font-medium ${successRate >= 90 ? 'text-green-600' : successRate >= 70 ? 'text-yellow-600' : 'text-red-600'}`}>
                              {successRate}%
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      <NotificationComponent />
    </div>
  );
};

export default Consumo;







